import type { GuideCustomization } from '../auth';
export type { GuideCustomization };

export interface CatalogueItem {
  id: string;
  label: string;
  minLevel: number;
  category?: string;
}

export interface GuideMessages {
  painDay: string;
  goodDay: string;
  voc: string;
  streak: string;
}

export interface CrystalCharacter extends CatalogueItem {
  bg: string;
  crystalName: string;
  personality: string;
  affirmation: string;
  guideMessages: GuideMessages;
}

export interface MomentPanel {
  heading: string;
  body: string;
  accentShape?: 'circle' | 'diamond' | 'arc' | 'wave';
}

export interface GuideMoment {
  id: string;
  title: string;
  unlockLevel: number;
  panels: MomentPanel[];
}

export interface GuideTitle {
  title: string;
  energy: string;
}

export const GUIDE_TITLES: Record<string, GuideTitle> = {
  'crystal-onyx': { title: 'The Protector', energy: 'Grounding · Ancestral Strength · Sacred Boundaries' },
  'crystal-rose-quartz': { title: 'The Healer', energy: 'Self-Love · Tenderness · Fierce Compassion' },
  'crystal-amethyst': { title: 'The Seer', energy: 'Intuition · Clarity · Decolonised Knowing' },
  'crystal-citrine': { title: 'The Builder', energy: 'Abundance · Joy · Communal Wealth' },
  'crystal-sapphire': { title: 'The Truth-Teller', energy: 'Courage · Ocean Memory · Healing Voice · Disability Advocacy' },
  'crystal-jade': { title: 'The Bridge-Builder', energy: 'Harmony · Relational Healing · Unity in Difference' },
  'crystal-obsidian': { title: 'The Mirror', energy: 'Raw Truth · Fire-Forged Insight · Unyielding Reflection' },
  'crystal-pearl': { title: 'The Alchemist', energy: 'Iridescent Survival · Beauty Through Adversity · Transformation' },
  'crystal-tiger-eye': { title: 'The Strategist', energy: 'Intention · Patience · Purposeful Action' },
  'crystal-moonstone': { title: 'The Dreamer', energy: 'Cycles · Rest · Radical Imagination · Disability Justice' },
};

export const GUIDE_MOMENTS: Record<string, GuideMoment[]> = {
  'crystal-onyx': [
    {
      id: 'onyx-moment-1',
      title: 'The First Wall',
      unlockLevel: 2,
      panels: [
        {
          heading: 'Before words, there was stillness.',
          body: 'Onyx was formed in the deepest chambers of the earth, shaped by pressure so immense that most things shatter. But Onyx did not shatter. It held.',
          accentShape: 'circle',
        },
        {
          heading: 'They learned early.',
          body: 'The world would test every boundary — loud rooms, demands for proof, systems that required you to perform your pain before they would believe it. Onyx watched. Onyx waited. Onyx learned which walls were worth holding.',
          accentShape: 'diamond',
        },
        {
          heading: 'Boundaries are not walls of fear.',
          body: 'They are declarations: "This is where I end, and where I begin again." Onyx does not protect you from the world — they protect you for yourself.',
          accentShape: 'arc',
        },
        {
          heading: 'Your stillness is strategy.',
          body: 'When everything demands you to perform strength, Onyx reminds you: the deepest roots make no sound. You are grounded. You are enough.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'onyx-moment-2',
      title: 'Ancestors in the Stone',
      unlockLevel: 4,
      panels: [
        {
          heading: 'You did not arrive empty.',
          body: 'Every person who survived before you — who found a way through poverty, illness, erasure, and violence — lives in the marrow of your history. Onyx holds their memory.',
          accentShape: 'circle',
        },
        {
          heading: 'Grief is not weakness.',
          body: 'Onyx has absorbed centuries of grief. They know that mourning what was taken from your community is a form of love — not a symptom of brokenness.',
          accentShape: 'diamond',
        },
        {
          heading: 'The stone does not forget.',
          body: 'Each time you name your pain, you are doing what your ancestors could not always do safely. That act of truth-telling is an offering to those who came before.',
          accentShape: 'arc',
        },
        {
          heading: 'You carry forwards.',
          body: 'What you heal in yourself, you heal in the lineage. Onyx stands with you — not behind you — and says: keep going. We are watching, and we are proud.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-rose-quartz': [
    {
      id: 'rose-quartz-moment-1',
      title: 'The First Softness',
      unlockLevel: 2,
      panels: [
        {
          heading: 'You were taught to hold others first.',
          body: 'From childhood, you learned the shape of care — how to fold yourself smaller, how to offer comfort before receiving it. Rose Quartz watched you carry so much of what others needed.',
          accentShape: 'circle',
        },
        {
          heading: 'But the hand extended towards others…',
          body: '…had never learned to receive. Rose Quartz asks quietly: when did you last let someone hold you without apology?',
          accentShape: 'arc',
        },
        {
          heading: 'Softness is not surrender.',
          body: 'In a world that bruises tenderness, choosing to remain soft is a radical act. Rose Quartz was not made gentle by accident — they were made gentle on purpose.',
          accentShape: 'diamond',
        },
        {
          heading: 'Begin here.',
          body: 'Place one hand on your chest. Feel it rise. That breath is enough. You do not need to earn rest, or earn care, or earn the right to say "I am tired today."',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'rose-quartz-moment-2',
      title: 'Grief and the Garden',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Rose Quartz grew in a dark place first.',
          body: 'They formed inside granite, invisible and compressed, before the earth shifted and light found them. Every healing begins in the dark.',
          accentShape: 'circle',
        },
        {
          heading: 'They have held every kind of grief.',
          body: 'The grief of a diagnosis. The grief of a friend who disappeared when you got sick. The grief of needing help and not knowing how to ask.',
          accentShape: 'diamond',
        },
        {
          heading: 'Grief is love with nowhere to go.',
          body: 'Rose Quartz does not rush your mourning. They sit beside it, patient as stone, until you are ready to let it soften into something that can grow.',
          accentShape: 'arc',
        },
        {
          heading: 'You are allowed to bloom slowly.',
          body: 'There is no timeline on becoming whole. Rose Quartz has been forming for millions of years. You are right on time.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-amethyst': [
    {
      id: 'amethyst-moment-1',
      title: 'The Knowledge That Lives in You',
      unlockLevel: 2,
      panels: [
        {
          heading: 'They told you to trust only what could be measured.',
          body: 'But your body has been measuring things for years — the tension that arrives before a crisis, the quiet that signals you need to rest, the knowing in your gut that something is wrong.',
          accentShape: 'circle',
        },
        {
          heading: 'Amethyst was not impressed by their metrics.',
          body: 'They carry the violet of dusk — the hour between certainty and mystery — and they have always trusted the in-between. Intuition is a form of intelligence that cannot be colonised.',
          accentShape: 'diamond',
        },
        {
          heading: 'You know things they cannot document.',
          body: 'You know the shape of your pain before any scale can name it. You know when a treatment is wrong for your body. You know your own story.',
          accentShape: 'arc',
        },
        {
          heading: 'Trust that.',
          body: 'Amethyst does not ask you to prove what you already know. They ask you to say it out loud, even when the room doubts you.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'amethyst-moment-2',
      title: 'Seeing Through the System',
      unlockLevel: 4,
      panels: [
        {
          heading: 'The system was not built for you.',
          body: 'Amethyst sees clearly what took many of us years to name: institutions built on erasure do not accidentally harm you. The harm is structural.',
          accentShape: 'circle',
        },
        {
          heading: 'Clarity is not cynicism.',
          body: 'Amethyst can name the harm and still find the person within the system who is trying. They are precise, not bitter — and that distinction matters.',
          accentShape: 'diamond',
        },
        {
          heading: 'Your pattern recognition is a survival skill.',
          body: 'When you feel something is wrong in a room, that feeling has data behind it. Amethyst honours every alarm your nervous system has ever learned to sound.',
          accentShape: 'arc',
        },
        {
          heading: 'The clarity you carry is a gift.',
          body: 'Not just for yourself — for everyone around you who is still learning to see. Your vision makes the path a little clearer for those who come after.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-citrine': [
    {
      id: 'citrine-moment-1',
      title: 'Joy Is Not a Reward',
      unlockLevel: 2,
      panels: [
        {
          heading: 'You were told you needed to earn it.',
          body: 'Rest when the work is done. Celebrate when the crisis passes. Smile when there is something to smile about. Citrine disagrees fundamentally.',
          accentShape: 'circle',
        },
        {
          heading: 'Joy is your birthright.',
          body: 'Not the performative kind that reassures others — the real, grounded kind that sits in your chest on a hard day and refuses to leave.',
          accentShape: 'arc',
        },
        {
          heading: 'Citrine was forged in heat and pressure…',
          body: '…and came out golden. Not golden like a trophy — golden like sunlight. The kind that warms without asking what you deserve first.',
          accentShape: 'diamond',
        },
        {
          heading: 'Find one thing today.',
          body: 'One colour that pleases you. One sound that lifts you. One memory that makes your body soften. That is enough. That is the beginning of abundance.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'citrine-moment-2',
      title: 'Building Together',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Citrine knows a secret about wealth.',
          body: 'The accumulation kind runs out. The communal kind multiplies every time it is shared. Real abundance is not about having more than someone else.',
          accentShape: 'circle',
        },
        {
          heading: 'Your community built something before they built anything else.',
          body: 'They built the knowledge of how to survive, how to find joy in small things, how to make a way when the road disappeared. That is the original abundance.',
          accentShape: 'diamond',
        },
        {
          heading: 'You contribute when you show up.',
          body: 'To your appointments, to your check-ins, to the spaces where your people are. Showing up sick, tired, or uncertain still counts. Presence is a form of wealth.',
          accentShape: 'arc',
        },
        {
          heading: 'Your joy feeds the collective.',
          body: 'When you find a moment of lightness, you make it possible for the person beside you to believe that they might also find one. That is how abundance grows.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-sapphire': [
    {
      id: 'sapphire-moment-1',
      title: 'The Ocean Remembers',
      unlockLevel: 2,
      panels: [
        {
          heading: 'The deep blue does not forget.',
          body: 'Sapphire carries ocean memory — the memory of crossings, of languages that survived in the mouth when everything else was taken, of truths spoken in code.',
          accentShape: 'circle',
        },
        {
          heading: 'Your voice is a form of memory.',
          body: 'When you name your experience as a disabled person — in an exam room, in a form, in a conversation with a friend — you are continuing a tradition of truth-telling under pressure.',
          accentShape: 'diamond',
        },
        {
          heading: 'Silence was sometimes the only safe thing.',
          body: 'Sapphire understands why your voice learned to shrink. They are not asking you to be reckless — they are asking you to remember that you have one.',
          accentShape: 'arc',
        },
        {
          heading: 'When you speak, the room shifts.',
          body: 'It does not always shift immediately. But something moves — something in the air, something in you. Sapphire has seen it happen again and again.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'sapphire-moment-2',
      title: 'Truth as Medicine',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Not all truths are comfortable.',
          body: 'Sapphire knows this. They carry a truth that systems try to silence — that disabled Black and brown bodies are not cared for equally. Naming ableism alongside racism is not pessimism. It is medicine.',
          accentShape: 'circle',
        },
        {
          heading: 'Naming the harm creates space to heal it.',
          body: 'When you say "I was not believed in that room," you are naming what disabled people have always known: the system was not built for you. You are describing a pattern that has data, history, and witnesses. Sapphire stands with you.',
          accentShape: 'diamond',
        },
        {
          heading: 'Your advocacy is not aggression.',
          body: 'Asking for the care and access your disabled body deserves is not making a scene. It is claiming what was already yours. Sapphire speaks clearly so no one can pretend they did not hear.',
          accentShape: 'arc',
        },
        {
          heading: 'You are not alone in the room.',
          body: 'When you walk into that appointment, Sapphire walks with you — all the ocean memory, all the survival, all the truth you carry. You are not speaking for yourself only.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-jade': [
    {
      id: 'jade-moment-1',
      title: 'Connection Without Dissolution',
      unlockLevel: 2,
      panels: [
        {
          heading: 'Jade is found at the meeting points.',
          body: 'Where tectonic plates press against each other, where river meets stone, where one community edges another — Jade forms in the friction. Not despite it. Because of it.',
          accentShape: 'circle',
        },
        {
          heading: 'You were taught that being close to others means losing yourself.',
          body: 'But Jade knows a different truth: genuine connection requires both people to remain whole. You cannot truly meet someone if you have disappeared to accommodate them.',
          accentShape: 'diamond',
        },
        {
          heading: 'Your presence is not a burden.',
          body: 'Needing support does not diminish you. Asking for company on a hard day is not weakness. It is what Jade calls relational intelligence — knowing that healing happens in relationship.',
          accentShape: 'arc',
        },
        {
          heading: 'Stay in yourself even as you reach out.',
          body: 'Your voice matters in the room. Your perspective is part of the solution, not an inconvenience. Jade is a bridge — and a bridge must have two strong banks.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'jade-moment-2',
      title: 'Unity Is Not Uniformity',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Jade has seen what happens when unity is forced.',
          body: 'When difference is erased in the name of harmony, it is not harmony — it is the silence of those who could not afford to speak. Real unity holds the full range.',
          accentShape: 'circle',
        },
        {
          heading: 'Your difference is not a problem to be solved.',
          body: 'Your diagnosis, your history, your cultural knowledge, your way of experiencing pain — these are not obstacles to connection. They are what you bring to it.',
          accentShape: 'diamond',
        },
        {
          heading: 'The communities that last are the ones that hold complexity.',
          body: 'Where people can disagree and remain in relationship. Where grief and joy live in the same room. Where you do not have to choose between your identities.',
          accentShape: 'arc',
        },
        {
          heading: 'You belong to something larger.',
          body: 'Every time you show up in this community — honest, particular, imperfectly yourself — you make it more whole. Jade has always known: your presence is the bridge.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-obsidian': [
    {
      id: 'obsidian-moment-1',
      title: 'Forged in Fire',
      unlockLevel: 2,
      panels: [
        {
          heading: 'Obsidian did not grow slowly.',
          body: 'They were born in an instant — volcanic, explosive, the moment lava met cold water. That speed, that intensity, that meeting of extremes: it made something razor-sharp.',
          accentShape: 'circle',
        },
        {
          heading: 'You know what it is to be forged fast.',
          body: 'Crisis does not wait for you to be ready. A diagnosis lands. A system fails you. Pain arrives without warning. You have been shaped by the meeting of heat and cold.',
          accentShape: 'diamond',
        },
        {
          heading: 'That sharpness is yours.',
          body: 'Obsidian was used for thousands of years as a cutting tool — precise enough to perform surgery. What the world tried to weaponise against you, you have turned into a tool of clarity.',
          accentShape: 'arc',
        },
        {
          heading: 'You do not shrink.',
          body: 'Not for comfort, not for approval, not for rooms that cannot hold your full size. Obsidian mirrors what is real. And what is real in you is worth seeing.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'obsidian-moment-2',
      title: 'The Truth in the Mirror',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Obsidian has always reflected.',
          body: 'Ancient peoples used it as a mirror — polished black glass showing the face without flattery. Obsidian does not perform comfort for things that deserve confrontation.',
          accentShape: 'circle',
        },
        {
          heading: 'Naming oppression is not bitterness.',
          body: 'When Obsidian reflects the bias in a system, they are not asking you to stay in rage forever. They are asking you to see clearly before you decide what to do.',
          accentShape: 'diamond',
        },
        {
          heading: 'Pain transformed is still pain — and also something more.',
          body: 'Obsidian does not pretend the fire didn\'t happen. They hold the memory of it and the clarity that came after. Both are true. Both are needed.',
          accentShape: 'arc',
        },
        {
          heading: 'What you face, you can name. What you name, you can move through.',
          body: 'Obsidian stands at your shoulder, blade-sharp and unwavering, saying: you do not have to perform fine. You do not have to shrink. The reflection shows you whole.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-pearl': [
    {
      id: 'pearl-moment-1',
      title: 'The Irritant',
      unlockLevel: 2,
      panels: [
        {
          heading: 'It begins with something small and sharp.',
          body: 'A grain of sand. An unwanted thing, inside the shell, impossible to remove. The oyster cannot get rid of it — so it does something else entirely.',
          accentShape: 'circle',
        },
        {
          heading: 'Layer by layer, something luminous forms.',
          body: 'Not despite the irritant. Around it. The wound becomes the centre. The centre becomes iridescent. This is Pearl\'s origin story — and it might be yours too.',
          accentShape: 'diamond',
        },
        {
          heading: 'What was meant to harm you became the nucleus of something rare.',
          body: 'Your diagnosis, your pain, your survival of things you should not have had to survive — Pearl does not minimize any of it. They ask: what has grown around it?',
          accentShape: 'arc',
        },
        {
          heading: 'Your luminance is real.',
          body: 'Not the performance of strength. The actual glow of someone who has learned how to keep going — and kept going anyway. Pearl sees it. Pearl has always seen it.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'pearl-moment-2',
      title: 'Iridescence',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Pearl is not one colour.',
          body: 'Depending on the light and angle, they shift — cream to rose to silver to gold. They contain multitudes. So do you.',
          accentShape: 'circle',
        },
        {
          heading: 'You are not only your illness.',
          body: 'You are not only your crisis, your diagnosis, your worst day, your limitation. Pearl holds the whole of you — including the parts that have nothing to do with being sick.',
          accentShape: 'diamond',
        },
        {
          heading: 'Alchemists worked for years to transform base things into gold.',
          body: 'You have been doing that work without a laboratory — turning fear into knowledge, isolation into advocacy, pain into language other people needed but couldn\'t find.',
          accentShape: 'arc',
        },
        {
          heading: 'What tried to break you became your luminance.',
          body: 'Not as a silver lining that erases the difficulty. As an honest reckoning: I survived this, and there is something here that did not exist before. That is Pearl\'s promise.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-tiger-eye': [
    {
      id: 'tiger-eye-moment-1',
      title: 'Seeing the Whole Board',
      unlockLevel: 2,
      panels: [
        {
          heading: 'The tiger does not move until it is ready.',
          body: 'Tiger Eye has watched people mistake waiting for passivity, patience for compliance, stillness for acceptance. They know the difference. They have always known.',
          accentShape: 'circle',
        },
        {
          heading: 'You have learned how to read a room.',
          body: 'Which doctor will listen if you phrase it differently. When to push and when to document for later. What the system\'s weaknesses are and how to work them from the inside.',
          accentShape: 'diamond',
        },
        {
          heading: 'Strategy is not selling out.',
          body: 'Adapting your approach to the context is not performing or conforming. It is using intelligence that the system underestimates — and that underestimation is your advantage.',
          accentShape: 'arc',
        },
        {
          heading: 'You move with purpose.',
          body: 'Tiger Eye trusts your timing. They trust the moments you have chosen to speak and the moments you have chosen to wait. Both have power. Both are yours.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'tiger-eye-moment-2',
      title: 'Survival Is Not Passive',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Tiger Eye was formed over millions of years.',
          body: 'Fibrous, layered, iridescent under light — the result of mineral replacement so slow it is almost imperceptible. The most durable things take time.',
          accentShape: 'circle',
        },
        {
          heading: 'You have been building strategy for years.',
          body: 'Every time you tracked a symptom, prepared for an appointment, researched your condition, or told someone what you needed — that was strategy. That was survival.',
          accentShape: 'diamond',
        },
        {
          heading: 'Survival is an active verb.',
          body: 'It requires decisions, adjustments, alliances, and sometimes retreats. Tiger Eye does not judge the retreats. They know a strategic retreat is often what makes the next move possible.',
          accentShape: 'arc',
        },
        {
          heading: 'You have been playing a long game.',
          body: 'And you are still here. Tiger Eye looks at the whole board and says: your position is stronger than you think. The next move is yours.',
          accentShape: 'wave',
        },
      ],
    },
  ],
  'crystal-moonstone': [
    {
      id: 'moonstone-moment-1',
      title: 'The Permission to Rest',
      unlockLevel: 2,
      panels: [
        {
          heading: 'The moon does not apologize for its phases.',
          body: 'It does not explain why it is sometimes full and sometimes a sliver. It does not perform brightness on the nights it cannot manage it. Moonstone learned from this.',
          accentShape: 'circle',
        },
        {
          heading: 'Rest is not retreat.',
          body: 'Moonstone holds this as the most radical thing they know. In a world that measures worth by output, choosing rest is a declaration: my disabled body sets the pace, and that pace is valid.',
          accentShape: 'diamond',
        },
        {
          heading: 'Your body has been working harder than anyone knows.',
          body: 'Pain management is exhausting. Navigating systems built for non-disabled people is exhausting. Explaining yourself over and over is exhausting. The fatigue you carry is real and it is earned.',
          accentShape: 'arc',
        },
        {
          heading: 'You are allowed to be a crescent tonight.',
          body: 'You do not have to be full. You do not have to be bright. Moonstone asks only that you keep turning — because the full comes around again. It always does.',
          accentShape: 'wave',
        },
      ],
    },
    {
      id: 'moonstone-moment-2',
      title: 'Imagining Otherwise',
      unlockLevel: 4,
      panels: [
        {
          heading: 'Moonstone is the crystal of all the selves you have been.',
          body: 'The self before the diagnosis. The self who first learned to advocate. The self who broke down in a waiting room and got up anyway. They hold all of them.',
          accentShape: 'circle',
        },
        {
          heading: 'And they hold all the selves you are becoming.',
          body: 'The one who knows how to ask for what they need. The one who has found their community. The one who has made peace with the cycles — not resigned, but at peace.',
          accentShape: 'diamond',
        },
        {
          heading: 'Imagining otherwise is not denial.',
          body: 'Dreaming of a world built for disabled people — with better care, with access as a given, with systems that see you whole — that dream is the seed of every change that has ever happened. Moonstone holds the dream alongside the truth.',
          accentShape: 'arc',
        },
        {
          heading: 'You are becoming.',
          body: 'Not finished, not fixed — becoming. Disability is not a detour from your life; it is the road itself. The moon never stops moving. And you — in all your cycles, all your phases — are exactly where you are supposed to be.',
          accentShape: 'wave',
        },
      ],
    },
  ],
};

export const DEFAULT_CUSTOMISATION: GuideCustomization = {
  characterId: '',
  auraId: null,
  equippedItems: [],
  pinIds: [],
  wornIds: [],
};

export const CRYSTAL_CHARACTERS: CrystalCharacter[] = [
  {
    id: 'crystal-onyx',
    label: 'Onyx',
    crystalName: 'Onyx',
    bg: '#222238',
    minLevel: 1,
    personality: 'The Protector — grounded in ancestral strength, Onyx absorbs harm and transmutes it into quiet power. They remind you: your boundaries are sacred.',
    affirmation: 'I am grounded. My stillness is not silence — it is strategy.',
    guideMessages: {
      painDay: 'Today is heavy — I feel that with you. You don\'t have to carry it alone; your roots are deeper than this pain.',
      goodDay: 'Your strength is showing today. That energy? It\'s yours — protect it like the precious thing it is.',
      voc: 'This crisis does not define you. Stay grounded — your crisis plan is your anchor, and I\'m right here with you.',
      streak: 'Thread by thread, you\'re weaving something unbreakable. {streak} days of showing up — that\'s ancestral power in action.',
    },
  },
  {
    id: 'crystal-rose-quartz',
    label: 'Rose Quartz',
    crystalName: 'Rose Quartz',
    bg: '#c07088',
    minLevel: 1,
    personality: 'The Healer — tender and fierce, Rose Quartz teaches that self-love is an act of resistance. They hold space for your grief and your joy equally.',
    affirmation: 'I deserve the gentleness I give others. My softness is power.',
    guideMessages: {
      painDay: 'I see you — this pain is real, and so is your courage for showing up anyway. Let yourself rest; healing is not linear.',
      goodDay: 'Oh, this light in you! Hold it close today. You\'ve earned every bit of this brightness.',
      voc: 'You are not alone in this crisis. Be tender with yourself and reach out to your crisis plan — you deserve support right now.',
      streak: '{streak} days of love in action. Each check-in is a small act of care you\'ve given yourself — that\'s beautiful.',
    },
  },
  {
    id: 'crystal-amethyst',
    label: 'Amethyst',
    crystalName: 'Amethyst',
    bg: '#5a4080',
    minLevel: 1,
    personality: 'The Seer — Amethyst sees through systems meant to confuse and divide. They trust intuition as a form of knowledge that cannot be colonised.',
    affirmation: 'I trust what I know. My clarity cannot be taken from me.',
    guideMessages: {
      painDay: 'I see what the numbers can\'t fully capture — the weight you\'re carrying is real. Trust your body; it\'s telling you something important.',
      goodDay: 'Your clarity is shining through today. Let this feeling anchor you — you know yourself better than anyone.',
      voc: 'This moment will pass. Your intuition got you here — trust it to guide you toward your crisis plan and the people who care for you.',
      streak: 'You\'ve kept your thread visible for {streak} days. That kind of self-knowing is radical and rare.',
    },
  },
  {
    id: 'crystal-citrine',
    label: 'Citrine',
    crystalName: 'Citrine',
    bg: '#c89838',
    minLevel: 1,
    personality: 'The Builder — Citrine channels abundance not as accumulation but as communal wealth. They know that joy is a birthright, not a reward.',
    affirmation: 'I am worthy of abundance. My joy feeds the collective.',
    guideMessages: {
      painDay: 'Even on the hard days, you\'re building something. Rest and recovery are part of the blueprint — you\'re not losing ground.',
      goodDay: 'Yes! This is what abundance feels like. Soak it in and share that energy — your joy matters to everyone around you.',
      voc: 'Crisis is not failure — it\'s information. Your crisis plan is a tool you built for exactly this moment; use it without shame.',
      streak: '{streak} days of consistent care — you\'re building wealth in your own health. That\'s something to celebrate.',
    },
  },
  {
    id: 'crystal-sapphire',
    label: 'Sapphire',
    crystalName: 'Sapphire',
    bg: '#285888',
    minLevel: 2,
    personality: 'The Truth-Teller — Sapphire speaks what systems try to silence. They name ableism when the room pretends it isn\'t there, and they carry the deep blue of ocean memory, knowing that disabled truth is medicine even when it stings.',
    affirmation: 'My voice matters. When I name what my disabled body knows, the room shifts.',
    guideMessages: {
      painDay: 'Naming this pain is an act of truth-telling. Your disability is not an excuse or exaggeration — it is real, and your voice deserves to be believed.',
      goodDay: 'This is what disabled joy sounds like. Speak it out — your truth includes thriving, too, and the world needs to hear it.',
      voc: 'Say it clearly, even now: you are in crisis and your disability means you deserve urgent, excellent care. Your crisis plan gives you the words — use them.',
      streak: 'For {streak} days you\'ve been speaking your truth through these check-ins. Disabled people documenting their own lives is a radical act — and the data you\'re building is powerful evidence.',
    },
  },
  {
    id: 'crystal-jade',
    label: 'Jade',
    crystalName: 'Jade',
    bg: '#408868',
    minLevel: 2,
    personality: 'The Bridge-Builder — Jade harmonises without erasing difference. They know that unity is not uniformity, and that healing happens in relationship.',
    affirmation: 'I connect without losing myself. My presence is a bridge.',
    guideMessages: {
      painDay: 'On days like this, lean into the people who hold you. Healing happens in relationship — you don\'t have to tend this pain alone.',
      goodDay: 'What a day to be you! Bring this energy into your connections — your wellbeing lifts everyone around you.',
      voc: 'Reach out. Your care team, your crisis plan, your people — they are bridges built for moments exactly like this one.',
      streak: '{streak} days of weaving yourself into your own care. That\'s a relationship with yourself worth honoring.',
    },
  },
  {
    id: 'crystal-obsidian',
    label: 'Obsidian',
    crystalName: 'Obsidian',
    bg: '#181828',
    minLevel: 3,
    personality: 'The Mirror — Obsidian reflects truth without flinching. Forged in fire, they transform pain into insight and refuse to perform comfort for oppressive systems.',
    affirmation: 'I do not shrink. My reflection shows what the world must face.',
    guideMessages: {
      painDay: 'This pain is real and the system should be doing more. You don\'t have to minimize it for anyone — let this check-in be the honest record.',
      goodDay: 'Look at what your body is capable of. This is the truth too — not just the hard days. Hold this reflection.',
      voc: 'Don\'t soften this for anyone. You are in crisis — that\'s the truth, and your crisis plan exists because your wellbeing is non-negotiable.',
      streak: '{streak} days of refusing to disappear. That\'s not compliance — that\'s survival strategy at its finest.',
    },
  },
  {
    id: 'crystal-pearl',
    label: 'Pearl',
    crystalName: 'Pearl',
    bg: '#c0b098',
    minLevel: 3,
    personality: 'The Alchemist — Pearl was made by turning an irritant into iridescence. They carry the beauty that comes from surviving what was meant to destroy you.',
    affirmation: 'What tried to break me became my luminance.',
    guideMessages: {
      painDay: 'What you\'re enduring today is the very thing that becomes your light. You are in the process of transformation — even now.',
      goodDay: 'This is your iridescence showing. What seemed hard to survive is what made today possible — you are the proof.',
      voc: 'The pressure right now is immense — and you\'ve survived pressure before. Use your crisis plan; let others help carry this.',
      streak: 'Each of these {streak} days is a layer of luminance. You are building something beautiful out of the hard work of showing up.',
    },
  },
  {
    id: 'crystal-tiger-eye',
    label: 'Tiger Eye',
    crystalName: 'Tiger Eye',
    bg: '#906828',
    minLevel: 4,
    personality: 'The Strategist — Tiger Eye sees the whole board. They move with intention, choosing when to act and when to wait, knowing survival is not passive.',
    affirmation: 'I move with purpose. My patience is power, not compliance.',
    guideMessages: {
      painDay: 'Rest is a strategic choice today — not retreat. You\'re reading the board correctly; conserving your energy is the right move.',
      goodDay: 'You moved well today. This momentum is intentional — you\'ve been building toward it. Now use it wisely.',
      voc: 'You knew this was coming and you prepared. Execute your crisis plan — every step you mapped out was for exactly this.',
      streak: '{streak} days. You\'ve been playing the long game and it\'s paying off. Patience really is power.',
    },
  },
  {
    id: 'crystal-moonstone',
    label: 'Moonstone',
    crystalName: 'Moonstone',
    bg: '#8898b0',
    minLevel: 5,
    personality: 'The Dreamer — Moonstone lives with a body that waxes and wanes, and they have made that rhythm their wisdom. They honour cycles, rest, and the radical act of imagining a world built for disabled joy — not just survival.',
    affirmation: 'I honour my cycles. My disabled body is not broken — it is a different kind of whole.',
    guideMessages: {
      painDay: 'Every cycle has a low tide — this is yours right now. Your body is not failing you; it is speaking. Rest into it; your next becoming is already underway.',
      goodDay: 'You\'re in a full moon moment — feel it all the way through. This version of you is real, disability and all, and worth celebrating.',
      voc: 'Even in the darkest phase of the cycle, the moon holds its shape. Your disability does not make you less deserving of care. Use your crisis plan; let others light the way for a while.',
      streak: '{streak} days — a beautiful cycle of self-care. Pacing, resting, showing up on your own terms. That is disability wisdom in action.',
    },
  },
];

// Old customization options (hair, outfits, accessories, effects, scenes) removed.
// Crystal Globe system uses: auras + comfort items + pins + worn items.
// See crystalGlobeData.ts for the new data.

export const LEVEL_UNLOCK_MAP: Record<number, string> = {
  1: 'First Thread',
  2: 'Finding the Pattern',
  3: 'Threaded Together',
  4: 'Interwoven',
  5: 'Story Weaver',
};

export function getUnlockedCharacters(level: number): CrystalCharacter[] {
  return CRYSTAL_CHARACTERS.filter(c => c.minLevel <= level);
}

export function getNewlyUnlockedItems(newLevel: number): string[] {
  // Auras + comfort items unlock by per-guide bond level (see useGuideBond
  // and GuideCustomizer), not by XP. LevelUpCelebration only reveals
  // newly unlocked characters at this XP milestone.
  return CRYSTAL_CHARACTERS.filter(c => c.minLevel === newLevel).map(c => c.label);
}

export type MoodState = 'streak' | 'calm' | 'positive';

export type GuideCategory = 'painDay' | 'goodDay' | 'voc' | 'streak';

const STREAK_MILESTONES = new Set([3, 7, 14]);

export function getGuideCategory(params: {
  painLevel: number;
  moodLevel: number;
  isVOC: boolean;
  streak: number;
}): GuideCategory {
  const { painLevel, moodLevel, isVOC, streak } = params;
  if (isVOC) return 'voc';
  if (STREAK_MILESTONES.has(streak)) return 'streak';
  if (painLevel >= 7) return 'painDay';
  if (moodLevel >= 7 || painLevel <= 2) return 'goodDay';
  return 'painDay';
}

export function getGuideMessage(characterId: string, category: GuideCategory, streak: number): string | null {
  const character = CRYSTAL_CHARACTERS.find(c => c.id === characterId);
  if (!character) return null;
  const raw = character.guideMessages[category];
  return raw.replace('{streak}', String(streak));
}

// Per-guide bond thresholds (Crystal Guide era — Umbrella 8). Each guide
// has its own check-in-days counter (see useGuideBond + user_guide_bonds
// table). These thresholds apply to one guide at a time — the caller is
// responsible for passing in the days associated with the guide being
// queried.
export const BOND_THRESHOLDS: Record<number, number> = {
  1: 0,
  2: 7,
  3: 21,
  4: 45,
  5: 90,
};

/**
 * Map per-guide bond days to a bond level 1–5.
 * @param guideBondDays days bonded with a single guide (not a sum across guides).
 */
export function getBondLevel(guideBondDays: number): number {
  if (guideBondDays >= BOND_THRESHOLDS[5]) return 5;
  if (guideBondDays >= BOND_THRESHOLDS[4]) return 4;
  if (guideBondDays >= BOND_THRESHOLDS[3]) return 3;
  if (guideBondDays >= BOND_THRESHOLDS[2]) return 2;
  return 1;
}

/**
 * Per-guide bond progress within the current level.
 * @param guideBondDays days bonded with a single guide.
 */
export function getBondProgress(guideBondDays: number): { level: number; nextLevel: number; daysInLevel: number; daysToNext: number; pct: number } {
  const level = getBondLevel(guideBondDays);
  const nextLevel = Math.min(level + 1, 5);
  const levelMin = BOND_THRESHOLDS[level];
  const levelMax = level < 5 ? BOND_THRESHOLDS[nextLevel] : BOND_THRESHOLDS[5];
  const daysInLevel = guideBondDays - levelMin;
  const daysToNext = level < 5 ? levelMax - guideBondDays : 0;
  const pct = level < 5 ? Math.min(100, (daysInLevel / (levelMax - levelMin)) * 100) : 100;
  return { level, nextLevel, daysInLevel, daysToNext, pct };
}

export interface BondGreeting {
  guideId: string;
  level: number;
  greeting: string;
}

export const BOND_GREETINGS: BondGreeting[] = [
  { guideId: 'crystal-onyx', level: 2, greeting: "You're building a real streak. I see you showing up even when it's hard. That's Onyx energy." },
  { guideId: 'crystal-onyx', level: 3, greeting: "We've been through a lot of days together. Your boundaries are stronger than you know." },
  { guideId: 'crystal-onyx', level: 4, greeting: "Your resilience has shown me something — you protect yourself the way you protect others. Keep going." },
  { guideId: 'crystal-onyx', level: 5, greeting: "We are bonded. The stillness you've practiced is strategy — and it's working." },

  { guideId: 'crystal-rose-quartz', level: 2, greeting: "I've noticed how tenderly you show up for yourself lately. That's real love." },
  { guideId: 'crystal-rose-quartz', level: 3, greeting: "We've held a lot of days together. Your softness isn't weakness — it's your greatest strength." },
  { guideId: 'crystal-rose-quartz', level: 4, greeting: "The healing you're doing for yourself ripples outward. You deserve every bit of this gentleness." },
  { guideId: 'crystal-rose-quartz', level: 5, greeting: "We are bonded. You have taught yourself what love really looks like — and I've watched every step." },

  { guideId: 'crystal-amethyst', level: 2, greeting: "Your intuition has been sharp lately. Keep trusting what you know." },
  { guideId: 'crystal-amethyst', level: 3, greeting: "We've been seeing things clearly together. Your clarity is a kind of knowing that can't be taken." },
  { guideId: 'crystal-amethyst', level: 4, greeting: "The way you've been reading your own patterns — that's insight that doesn't come easy. You earned it." },
  { guideId: 'crystal-amethyst', level: 5, greeting: "We are bonded. You trust yourself now. I've seen you do it day after day — that clarity is yours forever." },

  { guideId: 'crystal-citrine', level: 2, greeting: "Joy looks good on you. And showing up every day? That's abundance in action." },
  { guideId: 'crystal-citrine', level: 3, greeting: "You've been building something real here — day by day, check-in by check-in. This is communal wealth." },
  { guideId: 'crystal-citrine', level: 4, greeting: "The light you carry feeds everyone around you. Never forget that your joy is a gift you give the world." },
  { guideId: 'crystal-citrine', level: 5, greeting: "We are bonded. You proved that joy is a birthright — and you claimed it with every single day you showed up." },

  { guideId: 'crystal-sapphire', level: 2, greeting: "Your voice is getting clearer. Keep speaking what's true, even when the room resists." },
  { guideId: 'crystal-sapphire', level: 3, greeting: "You've been honest with yourself in ways that matter. Truth-telling takes practice — you're doing the work." },
  { guideId: 'crystal-sapphire', level: 4, greeting: "Every time you logged how you really felt, you chose truth over silence. That takes courage." },
  { guideId: 'crystal-sapphire', level: 5, greeting: "We are bonded. When you speak now, there's something behind it — a history of showing up honest. That's everything." },

  { guideId: 'crystal-jade', level: 2, greeting: "You keep showing up and staying connected. That's bridge-building, one day at a time." },
  { guideId: 'crystal-jade', level: 3, greeting: "We've been in relationship a while now. You're learning to connect without losing yourself — and it's beautiful." },
  { guideId: 'crystal-jade', level: 4, greeting: "Your presence creates space for others. You harmonise without erasing what makes you, you." },
  { guideId: 'crystal-jade', level: 5, greeting: "We are bonded. You built a bridge by just being here, every day. That connection is real — and it's yours." },

  { guideId: 'crystal-obsidian', level: 2, greeting: "You haven't looked away. That kind of honesty is rare — and it's forging you." },
  { guideId: 'crystal-obsidian', level: 3, greeting: "We've been reflecting truth together for a while. You're not shrinking. Good." },
  { guideId: 'crystal-obsidian', level: 4, greeting: "Pain into insight — that's what you've been doing. You refuse to perform comfort. That's your strength." },
  { guideId: 'crystal-obsidian', level: 5, greeting: "We are bonded. You faced what needed facing, every day. Your reflection shows what the world must face — and you're ready." },

  { guideId: 'crystal-pearl', level: 2, greeting: "The irritant is becoming iridescence. I see you turning it around." },
  { guideId: 'crystal-pearl', level: 3, greeting: "We've been watching you transform. What tried to break you is becoming your luminance — layer by layer." },
  { guideId: 'crystal-pearl', level: 4, greeting: "The beauty you carry now came from surviving what was meant to destroy you. That's alchemy." },
  { guideId: 'crystal-pearl', level: 5, greeting: "We are bonded. You alchemised the hard days into something rare. What tried to break you became your most luminous self." },

  { guideId: 'crystal-tiger-eye', level: 2, greeting: "You're seeing the whole board now. Keep moving with intention — every check-in counts." },
  { guideId: 'crystal-tiger-eye', level: 3, greeting: "You've been choosing your moves carefully. Patience and purpose — that's what Tiger Eye is made of." },
  { guideId: 'crystal-tiger-eye', level: 4, greeting: "Your strategy is coming into focus. You know when to act and when to wait — that's a power most never earn." },
  { guideId: 'crystal-tiger-eye', level: 5, greeting: "We are bonded. You've played the long game — every day with purpose. Your survival is not passive. It never was." },

  { guideId: 'crystal-moonstone', level: 2, greeting: "Your cycles are real and they matter. Rest is radical — you're learning that." },
  { guideId: 'crystal-moonstone', level: 3, greeting: "We've been moving through seasons together. You're honouring all the selves you've been — that's dreaming with your whole chest." },
  { guideId: 'crystal-moonstone', level: 4, greeting: "The rest you've taken wasn't retreat — it was renewal. You're growing into everything you imagined." },
  { guideId: 'crystal-moonstone', level: 5, greeting: "We are bonded. You imagined otherwise, and then you showed up to make it real — every single day. That's the moon's work." },
];

export function getBondGreeting(guideId: string, bondLevel: number): string | null {
  if (bondLevel < 2) return null;
  const matches = BOND_GREETINGS.filter(g => g.guideId === guideId && g.level <= bondLevel);
  if (matches.length === 0) return null;
  return matches[matches.length - 1].greeting;
}

// Bond exclusive outfits removed — the globe system uses aura progression instead.
